import BadWordsNext from 'bad-words-next';
import en from 'bad-words-next/lib/en';

// Chess-specific terms that should NOT be filtered (whitelist)
const chessWhitelist = [
  'attack', 'attacks', 'attacking', 'attacked',
  'kill', 'kills', 'killing', 'killed',
  'sacrifice', 'sacrifices', 'sacrificing', 'sacrificed',
  'capture', 'captures', 'capturing', 'captured',
  'destroy', 'destroys', 'destroying', 'destroyed',
  'take', 'takes', 'taking', 'took',
  'beat', 'beats', 'beating',
  'crush', 'crushes', 'crushing', 'crushed',
  'smash', 'smashes', 'smashing', 'smashed',
  'dominate', 'dominates', 'dominating', 'dominated',
  'aggressive', 'aggression',
  'tactical', 'tactics',
  'blitz', 'bullet',
  'mate', 'mates', 'mating', 'mated', // checkmate related
  'fork', 'forks', 'forking', 'forked',
  'pin', 'pins', 'pinning', 'pinned',
  'skewer', 'skewers', 'skewering', 'skewered',
  'trap', 'traps', 'trapping', 'trapped',
  'threat', 'threats', 'threatening', 'threatened',
  'poison', 'poisoned', // poison pawn
  'sharp', 'sharper', 'sharpest', // sharp position
  'wild', 'wilder', 'wildest', // wild position
  'crazy', 'crazier', 'craziest', // crazy position
  'mad', 'madder', 'maddest', // mad position
  'brilliant', 'genius', // move descriptions
  'killer', // killer move
  'deadly', // deadly trap
  'explosive', // explosive position
  'violent', // violent tactics
  'brutal', // brutal attack
  'savage', // savage play
  'vicious', // vicious attack
  'nasty', // nasty trick
  'dirty', // dirty flag (time trouble)
  'cheap', // cheap trick
  'dirty', 'dirtier', 'dirtiest' // dirty play
];

// Additional common false positives in names/chess context
const additionalWhitelist = [
  'cox', 'dick', 'gay', 'hancock', 'cox', 'ass', // common surnames
  'chess', 'game', 'player', 'tournament', 'match',
  'white', 'black', 'piece', 'pieces',
  'king', 'queen', 'rook', 'bishop', 'knight', 'pawn',
  'castle', 'castling', 'castles',
  'check', 'checks', 'checking', 'checked',
  'draw', 'draws', 'drawing',
  'win', 'wins', 'winning', 'won',
  'lose', 'loses', 'losing', 'lost',
  'resign', 'resigns', 'resigning', 'resigned',
  'flag', 'flags', 'flagging', 'flagged' // time flag
];

// Combine all whitelist terms
const allWhitelistTerms = [...chessWhitelist, ...additionalWhitelist]
  .filter(word => word && word.trim()) // Remove empty strings
  .map(word => word.toLowerCase());

// Initialize the filter with English language and exclusions (whitelist)
const filter = new BadWordsNext({ 
  data: en,
  exclusions: allWhitelistTerms
});

/**
 * Check if text contains bad words
 * @param {string} text - Text to check
 * @returns {boolean} - True if contains bad words, false otherwise
 */
export const containsBadWords = (text) => {
  if (!text || typeof text !== 'string') return false;
  return filter.check(text.trim());
};

/**
 * Filter bad words from text, replacing with asterisks
 * @param {string} text - Text to filter
 * @param {string} replacement - Replacement character (default: '*')
 * @returns {string} - Filtered text
 */
export const filterBadWords = (text, replacement = '***') => {
  if (!text || typeof text !== 'string') return text;
  return filter.filter(text.trim());
};

/**
 * Filter bad words with custom replacement text
 * @param {string} text - Text to filter
 * @param {string} replacementText - Custom replacement text (default: '[filtered]')
 * @returns {string} - Filtered text
 */
export const filterBadWordsWithText = (text, replacementText = '[filtered]') => {
  if (!text || typeof text !== 'string') return text;
  
  // Use filter method with callback to replace with custom text
  return filter.filter(text.trim(), () => replacementText);
};

/**
 * Sanitize participant data by filtering bad words from names and notes
 * @param {Array} participants - Array of participant objects
 * @returns {Array} - Array of participants with filtered content
 */
export const sanitizeParticipantData = (participants) => {
  if (!Array.isArray(participants)) return participants;
  
  return participants.map(participant => ({
    ...participant,
    fullName: filterBadWords(participant.fullName || ''),
    name: filterBadWords(participant.name || ''), // some might use 'name' instead of 'fullName'
    displayName: filterBadWords(participant.displayName || ''),
    notes: filterBadWords(participant.notes || ''),
    // Keep other fields unchanged
  }));
};

/**
 * React hook for bad words filtering in forms
 * @returns {Object} - Object containing filtering functions
 */
export const useBadWordsFilter = () => {
  const validateField = (value, fieldName = 'field') => {
    if (containsBadWords(value)) {
      return `Innapropriate language detected.`;
    }
    return null;
  };

  const validateForm = (formData, fieldsToCheck = []) => {
    const errors = {};
    
    fieldsToCheck.forEach(field => {
      const error = validateField(formData[field], field);
      if (error) {
        errors[field] = error;
      }
    });
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  return {
    containsBadWords,
    filterBadWords,
    filterBadWordsWithText,
    sanitizeParticipantData,
    validateField,
    validateForm
  };
};

// Export the filter instance for advanced usage
export { filter };

// Default export for convenience
const badWordsFilterUtils = {
  containsBadWords,
  filterBadWords,
  filterBadWordsWithText,
  sanitizeParticipantData,
  useBadWordsFilter,
  filter
};

export default badWordsFilterUtils;